import React, {useEffect, useState} from 'react';
import Table from '../../../components/common/table/Table';
import {ProjectSort, ProjectStatus, ProjectTarget} from '../../../types/project';
import {adminProjectTableAPI} from "../../../services/admin";
import {GetAdminProjectTableResponseData} from "../../../types/admin";
import {Column} from "../../common/table/TableSection";
import DateRangeFilter from '../../common/table/DateRangeFilter';
import {useNavigate} from "react-router-dom";
import styles from "../../client/document/ClientDocumentTable.module.css";
import Button from "../../common/button/Button";

interface DateRange {
    start: Date | null;
    end: Date | null;
}

interface DateRanges {
    create: DateRange;
    start: DateRange;
    end: DateRange;
}

//어드민 프로젝트 테이블
const AdminProjectTable = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<GetAdminProjectTableResponseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    const [searchType, setSearchType] = useState<ProjectTarget>(ProjectTarget.ALL);
    const [filters, setFilters] = useState<Record<string, any>>({
        status: ['예정', '진행중', '완료'],
        create: 'ALL',
        start: 'ALL',
        end: 'ALL'
    });

    //날짜 필터 값
    const [dateRanges, setDateRanges] = useState<DateRanges>({
        create: {start: null, end: null},
        start: {start: null, end: null},
        end: {start: null, end: null}
    });

    //정렬
    const [sort, setSort] = useState<{ field: string | null, direction: 'asc' | 'desc' | null }>({
        field: 'companyName',
        direction: 'asc'
    });

    // 리셋 상태 추가
    const [isResetting, setIsResetting] = useState(false);

    // 필터 전체 초기화
    const resetAllFilters = () => {
        setIsResetting(true);

        // 필터 초기화
        setFilters({
            status: ['예정', '진행중', '완료'],
            create: 'ALL',
            start: 'ALL',
            end: 'ALL'
        });

        // 날짜 범위 초기화
        setDateRanges({
            create: { start: null, end: null },
            start: { start: null, end: null },
            end: { start: null, end: null }
        });

        // 검색 관련 초기화
        setSearchTerm('');
        setPendingSearchTerm('');
        setSearchType(ProjectTarget.ALL);

        // 페이지 초기화
        setPage(1);

        // 정렬 초기화
        setSort({
            field: 'companyName',
            direction: 'asc'
        });

        // 다음 렌더링 사이클에서 리셋 상태 해제
        setTimeout(() => setIsResetting(false), 0);
    };

    //테이블 로우 선택 시
    const handleRowClick = (row: GetAdminProjectTableResponseData) => {
        //어드민 프로젝트 상세 페이지로 이동
        navigate(`/admin/projects/${row.projectId}`);
    };

    //컬럼
    const columns: Column<GetAdminProjectTableResponseData>[] = [
        {
            key: 'companyName',
            header: '회사명',
            width: '15%',
            sortable: true
        },
        {
            key: 'projectName',
            header: '프로젝트명',
            width: '15%',
            sortable: true
        },
        {
            key: 'projectStatus',
            header: '작업 여부',
            width: '8%'
        },
        {
            key: 'progress',
            header: '진행 척도',
            width: '8%',
            render: (row) => `${row}%`
        },
        {
            key: 'createdAt',
            header: '작업 생성일',
            width: '14%',
            sortable: true
        },
        {
            key: 'startedAt',
            header: '작업 시작일',
            width: '14%',
            sortable: true
        },
        {
            key: 'deadline',
            header: '작업 종료일',
            width: '14%',
            sortable: true
        },
        {
            key: 'dataAmount',
            header: '데이터 수량',
            width: '18%',
            sortable: true
        }
    ];

    //프로젝트 상태 필터
    const statusFilterOptions = [
        {label: '예정', value: '예정'},
        {label: '진행중', value: '진행중'},
        {label: '완료', value: '완료'}
    ];

    //API 사용 위한 날짜 데이터 형식 변환
    const formatDateForAPI = (date: Date | string | null): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    //API 사용 위한 날짜 범위 데이터 형식 변환
    const formatDateRangeForAPI = (start: Date | null, end: Date | null): string | null => {
        //선택 안한 경우 'ALL'로 표시
        if (!start && !end) return 'ALL';
        if (!end) return `${formatDateForAPI(start)}~`;
        //시작날짜~종료날짜 형식으로 변환
        return `${formatDateForAPI(start)}~${formatDateForAPI(end)}`;
    };

    //날짜 형식 변환
    const handleDateRangeChange = (key: string, start: Date | null, end: Date | null) => {
        // 리셋 중일 때는 날짜 변경 로직 건너뛰기
        if (isResetting) return;

        setDateRanges(prev => ({
            ...prev,
            [key]: {start, end}
        }));

        const dateRangeString = formatDateRangeForAPI(start, end);
        setFilters(prev => ({
            ...prev,
            [key]: dateRangeString
        }));

        setPage(1);
    };

    //테이블 필터 설정
    const tableFilters = [
        {
            key: 'status',
            label: '작업 여부',
            options: statusFilterOptions,
            multiple: true,
            value: filters.status,
            component: undefined
        },
        {
            key: 'create',
            label: '작업 생성일',
            component: (
                <DateRangeFilter
                    label="작업 생성일"
                    startDate={dateRanges.create.start}
                    endDate={dateRanges.create.end}
                    onDateChange={(start, end) => handleDateRangeChange('create', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'start',
            label: '작업 시작일',
            component: (
                <DateRangeFilter
                    label="작업 시작일"
                    startDate={dateRanges.start.start}
                    endDate={dateRanges.start.end}
                    onDateChange={(start, end) => handleDateRangeChange('start', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'end',
            label: '작업 종료일',
            component: (
                <DateRangeFilter
                    label="작업 종료일"
                    startDate={dateRanges.end.start}
                    endDate={dateRanges.end.end}
                    onDateChange={(start, end) => handleDateRangeChange('end', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        }
    ];

    //정렬 기준 설정
    const getSortParam = (field: string | null, direction: 'asc' | 'desc' | null): ProjectSort => {
        if (!field || !direction) return ProjectSort.NAME_ASC;
        const sortMap: Record<string, Record<string, ProjectSort>> = {
            companyName: {
                asc: ProjectSort.COMPANY_ASC,
                desc: ProjectSort.COMPANY_DESC
            },
            projectName: {
                asc: ProjectSort.NAME_ASC,
                desc: ProjectSort.NAME_DESC
            },
            createdAt: {
                asc: ProjectSort.CREATE_ASC,
                desc: ProjectSort.CREATE_DESC
            },
            startedAt: {
                asc: ProjectSort.START_ASC,
                desc: ProjectSort.START_DESC
            },
            deadline: {
                asc: ProjectSort.END_ASC,
                desc: ProjectSort.END_DESC
            },
            dataAmount: {
                asc: ProjectSort.AMOUNT_ASC,
                desc: ProjectSort.AMOUNT_DESC
            }
        };
        return sortMap[field]?.[direction] || ProjectSort.NAME_ASC;
    };

    //프로젝트 생성 페이지
    const handleAdd = () => {
        navigate("/admin/projects/create");
    }

    //검색
    const handleSearch = () => {
        setSearchTerm(pendingSearchTerm);
        setPage(1);
    };

    //엔터키 사용 검색
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    //데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //어드민 프로젝트 테이블 API
            const response = await adminProjectTableAPI({
                page: page - 1,
                sort: getSortParam(sort.field, sort.direction),
                search: searchTerm,
                target: searchType,
                status: filters.status as ProjectStatus[],
                create: filters.create,
                start: filters.start,
                end: filters.end
            });
            //데이터 설정
            setData(response.content);
            //페이지 설정
            setTotalPages(Math.ceil(response.total / 7));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, searchTerm, searchType, sort, filters]);

    //검색 대상 설정
    const searchOptions = [
        {value: ProjectTarget.ALL, label: '전체'},
        {value: ProjectTarget.COMPANY_NAME, label: '회사명'},
        {value: ProjectTarget.PROJECT_NAME, label: '프로젝트명'}
    ];

    //필터값 변경
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => {
            //프로젝트 상태 필터 변경
            if (key === 'status') {
                const newStatuses = value;
                if (newStatuses.length === 0) {
                    return {...prev, [key]: ['예정', '진행중', '완료']};
                }
                return {...prev, status: newStatuses};
            }
            return {...prev, [key]: value};
        });
        setPage(1);
    };

    //정렬 변경
    const handleSortChange = (field: string | null, direction: 'asc' | 'desc' | null) => {
        setSort({field, direction});
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <div className={styles.titleContainer}>
                    <div className={styles.padding}/>
                    <h3>프로젝트</h3>
                    <Button
                        variant="primary"
                        onClick={handleAdd}
                        size={"small"}
                        className={styles.mobileButton}
                    >
                        추가
                    </Button>
                </div>
                <div className={styles.searchInputContainer}>
                    <div>
                        {/*검색 대상 선택*/}
                        <select
                            value={searchType}
                            onChange={(e) => setSearchType(e.target.value as ProjectTarget)}
                            className={styles.searchTypeSelect}
                        >
                            {searchOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.searchInputWrapper}>
                        {/*검색 창*/}
                        <input
                            type="text"
                            placeholder="검색어를 입력하세요"
                            value={pendingSearchTerm}
                            onChange={(e) => setPendingSearchTerm(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className={styles.searchInput}
                        />
                        {/*검색 버튼*/}
                        <Button
                            onClick={handleSearch}
                            className={styles.searchButton}
                            variant="primary"
                            size={"small"}
                        >
                            검색
                        </Button>
                    </div>
                </div>
                {/*생성 버튼*/}
                <Button
                    variant="primary"
                    onClick={handleAdd}
                    size={"small"}
                    className={styles.pcButton}
                >
                    추가
                </Button>
            </div>
            {/*테이블*/}
            <Table
                columns={columns}
                data={data}
                loading={loading}
                size="medium"
                filters={tableFilters}
                showPagination={true}
                page={page}
                pageSize={7}
                totalItems={totalPages * 7}
                onPageChange={setPage}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onRowClick={handleRowClick}
                onReset={resetAllFilters}
            />
        </div>
    );
};

export default AdminProjectTable;